import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Popover
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useAssessmentLibrary } from '../../../hooks/resources/useAssessmentLibrary';

const AssessmentPicker = ({
  value,
  onChange,
  onAssessmentSelect,  // Optional callback with full assessment object for score range info
  label = 'Select Assessment',
  required = false,
  helperText = '',
  error = false
}) => {
  const { assessments, loading, error: fetchError } = useAssessmentLibrary();
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [infoAnchor, setInfoAnchor] = useState(null);

  useEffect(() => {
    if (value && assessments.length > 0) {
      const assessment = assessments.find(a => a.assessmentKey === value);
      setSelectedAssessment(assessment);
      // Also notify parent of initial assessment for score range info
      if (assessment && onAssessmentSelect) {
        onAssessmentSelect(assessment);
      }
    } else if (!value) {
      setSelectedAssessment(null);
    }
  }, [value, assessments, onAssessmentSelect]);

  const handleChange = (event, newValue) => {
    setSelectedAssessment(newValue);
    // Pass both assessmentKey and full assessment object for score range info
    onChange(newValue?.assessmentKey || null, newValue);
  };

  const handleInfoClick = (event) => {
    event.stopPropagation();
    setInfoAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchor(null);
  };

  return (
    <Box>
      <Autocomplete
        value={selectedAssessment}
        onChange={handleChange}
        options={assessments}
        getOptionLabel={(option) => option.name || ''}
        loading={loading}
        isOptionEqualToValue={(option, value) => option.assessmentKey === value?.assessmentKey}
        componentsProps={{
          popper: {
            sx: { zIndex: 1500 }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error || !!fetchError}
            helperText={fetchError?.message || helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {selectedAssessment && (
                    <IconButton size="small" onClick={handleInfoClick}>
                      <Info fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {option.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.questionCount} questions • ~{option.averageCompletionMinutes} min
                </Typography>
              </Box>
              {option.type && (
                <Chip
                  size="small"
                  label={option.type}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          );
        }}
      />

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedAssessment && (
          <Box sx={{ p: 2, maxWidth: 450 }}>
            <Typography variant="h6" gutterBottom>
              {selectedAssessment.name}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Type:</strong> {selectedAssessment.type}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Questions:</strong> {selectedAssessment.questionCount}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Avg Time:</strong> {selectedAssessment.averageCompletionMinutes} minutes
              </Typography>
            </Box>

            {selectedAssessment.domains && selectedAssessment.domains.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  <strong>Domains:</strong>
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedAssessment.domains.map((domain, idx) => (
                    <Chip key={idx} label={domain} size="small" sx={{ fontSize: '0.7rem' }} />
                  ))}
                </Box>
              </Box>
            )}

            {selectedAssessment.scoringBands && selectedAssessment.scoringBands.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  <strong>Scoring Bands:</strong>
                </Typography>
                {selectedAssessment.scoringBands.map((band, idx) => (
                  <Box key={idx} sx={{ ml: 1, mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>{band.band}:</strong> {band.range[0]}-{band.range[1]} - {band.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {selectedAssessment.usageCount && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                <strong>Usage:</strong> {selectedAssessment.usageCount.flows} flows, {selectedAssessment.usageCount.completions} completions
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default AssessmentPicker;
